import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "../../utils/apiError";
import "./TacticEditor.css";
import "./TacticList.css";

/**
 * Empty-state quick-start dialog: name the generic tactic, optionally assign it
 * to all players, and (when assigning) show the server + goalkeeper reminder.
 *
 * @param {{
 *   defaultName: string,
 *   onConfirm: (name: string, assignAll: boolean) => Promise,
 *   onClose: () => void,
 * }} props
 */
export function BasicTacticModal({ defaultName, onConfirm, onClose }) {
  const { t } = useTranslation();
  const [name, setName]         = useState(defaultName);
  const [assignAll, setAssign]  = useState(true);
  const [error, setError]       = useState("");
  const [saving, setSaving]     = useState(false);

  const confirm = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError(t("tactic.nameRequired")); return; }
    setSaving(true);
    setError("");
    try {
      await onConfirm(trimmed, assignAll);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>

        <div className="modal-header">
          <span className="modal-title">{t("basic.createButton")}</span>
          <button className="modal-close-btn" onClick={onClose} disabled={saving}>✕</button>
        </div>

        <input
          className={`tactic-name-input basic-name-input ${error ? "input-error" : ""}`}
          value={name}
          autoFocus
          maxLength={30}
          disabled={saving}
          onChange={(e) => { setName(e.target.value); setError(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") confirm(); if (e.key === "Escape") onClose(); }}
          placeholder={t("tactic.namePlaceholder")}
        />

        <label className="basic-assign-toggle">
          <input
            type="checkbox"
            checked={assignAll}
            disabled={saving}
            onChange={(e) => setAssign(e.target.checked)}
          />
          {t("basic.assignAll")}
        </label>

        {assignAll && <p className="basic-role-hint">{t("basic.roleHint")}</p>}

        {error && <div className="tactic-modal-error">{error}</div>}

        <div className="modal-footer">
          <div />
          <div className="modal-actions">
            <button className="modal-cancel-btn" onClick={onClose} disabled={saving}>{t("cancel")}</button>
            <button className="modal-save-btn" onClick={confirm} disabled={saving}>{t("save")}</button>
          </div>
        </div>

      </div>
    </div>
  );
}
